import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPathForTab } from '@/config/routes';
import axios from '../utils/axiosInstance';
import { BASE_URL } from '@/config/constants';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { 
  Users, 
  Globe, 
  Palette,
  Share2,
  Activity,
  Server,
  Database,
  HardDrive,
  Clock,
  TrendingUp,
  CheckCircle2,
  XCircle,
  ArrowRight,
  RefreshCw,
  BarChart3,
  PieChart,
  Music,
  Video,
  Image,
  Mail,
  Calendar,
  Eye,
  UserCheck,
  Zap,
  Settings,
  CheckCircle,
} from 'lucide-react';

import { toast } from 'sonner';
import { openPreview } from '@/utils/preview';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from '@/components/ui/chart';

interface PendingDomain {
  _id: string;
  slug: string;
  approvalStatus: string;
  hasCompletedOnboarding: boolean;
  createdAt: string;
}

interface SuperAdminDashboardProps {
  user: {
    _id: string;
    username: string;
    fullName?: string;
    user_type: string;
    email: string;
    domain: string;
  } | null;
  setTab: (tab: string) => void;
}

interface SystemStats {
  totalDomains: number;
  activeDomains: number;
  totalUsers: number;
  activeUsers: number;
  totalMasterThemes: number;
  totalMasterPlatforms: number;
  totalVisitors: number;
  totalUniqueVisitors: number;
}

interface DomainStats {
  slug: string;
  name?: string;
  totalContent: number;
  music: number;
  videos: number;
  images: number;
  leads: number;
  events: number;
  totalVisitors: number;
  uniqueVisitors: number;
  lastActivity?: string;
  isActive: boolean;
}

interface ContentOverview {
  totalMusic: number;
  totalVideos: number;
  totalImages: number;
  totalLeads: number;
  totalEvents: number;
  totalCategories: number;
}

interface RecentActivity {
  _id: string;
  type: 'lead' | 'content' | 'user' | 'domain';
  message: string;
  domain?: string;
  timestamp: string;
}

interface VisitorTrendsData {
  date: string;
  sessions: number;
  uniqueVisitors: number;
  newVisitors: number;
  returningVisitors: number;
  avgDurationSeconds: number;
  topEvents: { event: string; count: number }[];
}

interface VisitorTrends {
  period: { days: number | 'all' | 'today' | 'range'; from: string | null; to: string | null };
  daily: VisitorTrendsData[];
  comparison: {
    sessions: { current: number; previous: number; change: number | null };
    uniqueVisitors: { current: number; previous: number; change: number | null };
  };
}

interface AudienceBreakdownItem {
  name: string;
  sessions: number;
  uniqueVisitors: number;
}

interface AudienceBreakdown {
  period: { days: number; from: string };
  totalSessions: number;
  countries: AudienceBreakdownItem[];
  cities: AudienceBreakdownItem[];
  devices: AudienceBreakdownItem[];
  browsers: AudienceBreakdownItem[];
  oses: AudienceBreakdownItem[];
}

const visitorTrendsChartConfig = {
  sessions: {
    label: 'Total Visits',
    color: 'hsl(var(--primary))',
  },
  uniqueVisitors: {
    label: 'Unique Visitors',
    color: '#10b981',
  },
} satisfies ChartConfig;

// Persisted set of expanded super-admin dashboard card IDs.
// Separate localStorage key from the artist Dashboard so a super-admin's
// per-page choices don't collide when they switch tenants.
const SUPER_ADMIN_EXPANDED_KEY = 'artistcms.superAdminDashboardCards.expanded';
const readStoredExpanded = (fallback: string[]): string[] => {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(SUPER_ADMIN_EXPANDED_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : fallback;
  } catch {
    return fallback;
  }
};

// StatCard — renders as an `AccordionItem` for the "System Overview" grid.
// Declared at module scope (NOT inside SuperAdminDashboard) so each render
// doesn't create a fresh component type, which would remount every item and
// wipe Radix's `data-state` transition (breaking the expand animation).
//
// Collapsed header: icon + title + value badge.
// Expanded body: large value, subtitle, optional trend, and (if `onClick` is
// provided) a "View Details" navigation button.
interface StatCardProps {
  id: string;
  title: string;
  value: number | string;
  subtitle?: string;
  icon: any;
  onClick?: () => void;
  trend?: { value: number; positive: boolean };
}
const StatCard: React.FC<StatCardProps> = ({
  id,
  title,
  value,
  subtitle,
  icon: Icon,
  onClick,
  trend,
}) => (
  <AccordionItem value={id} className="group @container border-none">
    <Card className="p-0 overflow-hidden hover:shadow-none transition-shadow duration-200">
      <AccordionTrigger className="px-4 sm:px-5 py-4 hover:bg-muted/30 transition-colors">
        <Icon className="w-6 h-6 shrink-0 text-primary" />
        <h3 className="flex-1 min-w-0 text-left font-semibold text-sm sm:text-base text-foreground truncate">
          {title}
        </h3>
        <Badge variant="secondary" className="shrink-0 tabular-nums group-data-[state=open]:hidden">
          {value}
        </Badge>
      </AccordionTrigger>
      <AccordionContent className="px-4 sm:px-5 pb-4 pt-0">
        <div className="mb-1">
          <span className="text-2xl sm:text-3xl font-bold text-primary tabular-nums">
            {value}
          </span>
        </div>
        {subtitle && (
          <p className="text-xs text-muted-foreground mb-2">{subtitle}</p>
        )}
        {trend && (
          <div className={`flex items-center gap-1 text-xs mb-3 ${trend.positive ? 'text-green-600' : 'text-red-600'}`}>
            <TrendingUp className={`w-3 h-3 ${!trend.positive && 'rotate-180'}`} />
            <span>{trend.value}% from last month</span>
          </div>
        )}
        {onClick && (
          <Button
            variant="secondary"
            size="sm"
            className="hover:bg-primary hover:text-primary-foreground"
            onClick={onClick}
          >
            View Details
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </AccordionContent>
    </Card>
  </AccordionItem>
);

const SuperAdminDashboard: React.FC<SuperAdminDashboardProps> = ({ user, setTab: _setTab }) => {
  const navigate = useNavigate();
  const setTab = (tab: string) => navigate(getPathForTab(tab));
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [systemStats, setSystemStats] = useState<SystemStats>({
    totalDomains: 0,
    activeDomains: 0,
    totalUsers: 0,
    activeUsers: 0,
    totalMasterThemes: 0,
    totalMasterPlatforms: 0,
    totalVisitors: 0,
    totalUniqueVisitors: 0,
  });
  const [domainStats, setDomainStats] = useState<DomainStats[]>([]);
  const [contentOverview, setContentOverview] = useState<ContentOverview>({
    totalMusic: 0,
    totalVideos: 0,
    totalImages: 0,
    totalLeads: 0,
    totalEvents: 0,
    totalCategories: 0,
  });
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([]);
  const [pendingDomains, setPendingDomains] = useState<PendingDomain[]>([]);
  const [approvingSlug, setApprovingSlug] = useState<string | null>(null);
  const [visitorTrends, setVisitorTrends] = useState<VisitorTrends | null>(null);
  const [trendsDays, setTrendsDays] = useState<7 | 30 | 'all' | 'today' | 'range'>(30);
  const [trendsFrom, setTrendsFrom] = useState<string>('');
  const [trendsTo, setTrendsTo] = useState<string>('');
  const [trendsDomain, setTrendsDomain] = useState<string>('');
  const [trendsDomainOpen, setTrendsDomainOpen] = useState(false);
  const trendsDomainRef = React.useRef<HTMLDivElement>(null);
  const [trendsLoading, setTrendsLoading] = useState(false);
  const [audienceBreakdown, setAudienceBreakdown] = useState<AudienceBreakdown | null>(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [systemHealth, setSystemHealth] = useState({
    api: 'checking' as 'healthy' | 'degraded' | 'down' | 'checking',
    database: 'checking' as 'healthy' | 'degraded' | 'down' | 'checking',
    storage: 'checking' as 'healthy' | 'degraded' | 'down' | 'checking',
  });

  // Accordion expand state for System Overview + Audience Breakdown cards.
  // Default is empty (all collapsed) so first-time users see the compact
  // layout; their expand/collapse choices then persist per browser.
  const [expandedCards, setExpandedCards] = useState<string[]>(() => readStoredExpanded([]));
  const handleExpandedChange = (next: string[]) => {
    setExpandedCards(next);
    try {
      window.localStorage.setItem(SUPER_ADMIN_EXPANDED_KEY, JSON.stringify(next));
    } catch { /* localStorage unavailable — in-memory only */ }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (trendsDomainRef.current && !trendsDomainRef.current.contains(event.target as Node)) {
        setTrendsDomainOpen(false);
      }
    };
    if (trendsDomainOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [trendsDomainOpen]);

  useEffect(() => {
    fetchVisitorTrends();
  }, [trendsDays, trendsFrom, trendsTo, trendsDomain]);

  useEffect(() => {
    fetchAudienceBreakdown();
  }, [trendsDays]);

  const fetchAudienceBreakdown = async () => {
    setBreakdownLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/api/admin/audience-breakdown`, {
        params: { days: trendsDays === 'all' ? 'all' : trendsDays },
      });
      setAudienceBreakdown(res.data);
    } catch (error) {
      console.error('Error fetching audience breakdown:', error);
    } finally {
      setBreakdownLoading(false);
    }
  };

  const fetchVisitorTrends = async () => {
    setTrendsLoading(true);
    try {
      const params: Record<string, any> = {
        days: trendsDays === 'all' || trendsDays === 'today' || trendsDays === 'range' ? trendsDays : trendsDays,
      };
      if (trendsDays === 'range') {
        if (trendsFrom) params.from = trendsFrom;
        if (trendsTo) params.to = trendsTo;
      }
      if (trendsDomain) params.domain_slug = trendsDomain;
      const res = await axios.get(`${BASE_URL}/api/admin/visitor-trends`, { params });
      setVisitorTrends(res.data);
    } catch (error) {
      console.error('Error fetching visitor trends:', error);
    } finally {
      setTrendsLoading(false);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    setSystemHealth({
      api: 'checking',
      database: 'checking',
      storage: 'checking',
    });
    
    try {
      // Single aggregated API call replaces 60+ separate calls
      const [summaryRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/admin/super-dashboard-summary`),
        checkSystemHealth(),
      ]);

      const { systemStats: stats, domainStats: dStats, contentOverview: content, recentActivities: activities } = summaryRes.data;

      setSystemStats(stats);
      setDomainStats(dStats);
      setContentOverview(content);
      setRecentActivities(activities);

      // Fetch domains for pending review
      try {
        const domainsRes = await axios.get(`${BASE_URL}/api/domain`);
        const pending = (domainsRes.data || []).filter((d: PendingDomain) => d.hasCompletedOnboarding && d.approvalStatus === 'pending');
        setPendingDomains(pending);
      } catch { /* non-fatal */ }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
  };

  const handleQuickApprove = async (slug: string) => {
    setApprovingSlug(slug);
    try {
      await axios.patch(`${BASE_URL}/api/domain/${slug}/approve`);
      setPendingDomains(prev => prev.filter(d => d.slug !== slug));
      toast.success(`${slug} approved and published`);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Failed to approve');
    } finally {
      setApprovingSlug(null);
    }
  };

  const checkSystemHealth = async () => {
    const healthStatus = {
      api: 'down' as 'healthy' | 'degraded' | 'down',
      database: 'down' as 'healthy' | 'degraded' | 'down',
      storage: 'down' as 'healthy' | 'degraded' | 'down',
    };

    // Check API health using the dedicated health endpoint
    try {
      const response = await axios.get(`${BASE_URL}/health`, { timeout: 5000 });
      if (response.status === 200) {
        healthStatus.api = 'healthy';
        healthStatus.database = response.data?.mongo === 'connected' ? 'healthy' : 'degraded';
      } else {
        healthStatus.api = 'degraded';
        healthStatus.database = 'degraded';
      }
    } catch (error: any) {
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        healthStatus.api = 'degraded';
        healthStatus.database = 'degraded';
      } else {
        healthStatus.api = 'down';
        healthStatus.database = 'down';
      }
    }

    // Check storage/assets health
    try {
      const response = await axios.get(`${BASE_URL}/api/server-assets`, { timeout: 5000 });
      if (response.status === 200) {
        healthStatus.storage = 'healthy';
      } else {
        healthStatus.storage = 'degraded';
      }
    } catch (error: any) {
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        healthStatus.storage = 'degraded';
      } else {
        // Storage might just not have the endpoint, default to healthy if API is healthy
        healthStatus.storage = healthStatus.api === 'healthy' ? 'healthy' : 'down';
      }
    }

    setSystemHealth(healthStatus);
  };

  const HealthIndicator = ({ status }: { status: string }) => {
    const getStatusColor = () => {
      switch (status) {
        case 'healthy': return 'bg-green-500';
        case 'degraded': return 'bg-yellow-500';
        case 'down': return 'bg-red-500';
        case 'checking': return 'bg-gray-400';
        default: return 'bg-gray-500';
      }
    };

    return (
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${getStatusColor()} ${status === 'checking' ? 'animate-pulse' : ''}`} />
        <span className={`text-sm capitalize ${status === 'down' ? 'text-red-600 font-medium' : status === 'degraded' ? 'text-yellow-600' : ''}`}>
          {status}
        </span>
      </div>
    );
  };

  // Calculate overall system status
  const getOverallStatus = () => {
    const statuses = [systemHealth.api, systemHealth.database, systemHealth.storage];
    if (statuses.includes('down')) return 'down';
    if (statuses.includes('degraded')) return 'degraded';
    if (statuses.includes('checking')) return 'checking';
    return 'healthy';
  };

  const overallStatus = getOverallStatus();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center flex flex-col items-center gap-3">
          <Spinner size="2xl" />
          <p className="text-muted-foreground text-sm">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  const totalLeads = domainStats.reduce((sum, d) => sum + d.leads, 0);
  const totalContent = contentOverview.totalMusic + contentOverview.totalVideos + contentOverview.totalImages;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            {/* <div className="p-2 rounded-lg bg-primary">
              <Shield className="w-6 h-6 text-white" />
            </div> */}
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
                Super Admin Dashboard
              </h1>
              <p className="text-sm text-muted-foreground">
                Welcome back, {user?.fullName || user?.username}! Here's your system overview.
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <div className="text-right">
            <p className="text-sm text-muted-foreground">
              {new Date().toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
            <p className="text-xs text-muted-foreground">
              Last updated: {new Date().toLocaleTimeString()}
            </p>
          </div>
        </div>
      </div>

      {/* System Health Status */}
      <Card className="p-4 bg-linear-to-r from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3 sm:gap-6">
            <div className="flex items-center gap-2">
              <Activity className={`w-5 h-5 ${
                overallStatus === 'healthy' ? 'text-green-500' : 
                overallStatus === 'degraded' ? 'text-yellow-500' : 
                overallStatus === 'down' ? 'text-red-500' : 
                'text-gray-400'
              }`} />
              <span className="font-medium">System Status</span>
            </div>
            <div className="flex flex-wrap items-center gap-3 sm:gap-6">
              <div className="flex items-center gap-2">
                <Server className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">API:</span>
                <HealthIndicator status={systemHealth.api} />
              </div>
              <div className="flex items-center gap-2">
                <Database className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Database:</span>
                <HealthIndicator status={systemHealth.database} />
              </div>
              <div className="flex items-center gap-2">
                <HardDrive className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Storage:</span>
                <HealthIndicator status={systemHealth.storage} />
              </div>
            </div>
          </div>
          <Badge 
            variant="outline" 
            className={`${
              overallStatus === 'healthy' ? 'bg-green-50 text-green-700 border-green-200' :
              overallStatus === 'degraded' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
              overallStatus === 'down' ? 'bg-red-50 text-red-700 border-red-200' :
              'bg-gray-50 text-gray-700 border-gray-200'
            }`}
          >
            {overallStatus === 'healthy' ? 'All Systems Operational' :
             overallStatus === 'degraded' ? 'Some Systems Degraded' :
             overallStatus === 'down' ? 'System Issues Detected' :
             'Checking Systems...'}
          </Badge>
        </div>
      </Card>

      {/* Primary Stats */}
      <div>
        <h2 className="text-base sm:text-lg font-semibold mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          System Overview
        </h2>
        <Accordion
          type="multiple"
          value={expandedCards}
          onValueChange={handleExpandedChange}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          <StatCard 
            id="totalDomains"
            title="Total Domains" 
            value={systemStats.totalDomains}
            subtitle={`${systemStats.activeDomains} active`}
            icon={Globe} 
            onClick={() => setTab('domain')}
          />
          <StatCard 
            id="totalUsers"
            title="Total Users" 
            value={systemStats.totalUsers}
            subtitle={`${systemStats.activeUsers} active`}
            icon={Users} 
            onClick={() => setTab('user')}
          />
          <StatCard 
            id="totalVisitors"
            title="Total Visitors" 
            value={systemStats.totalVisitors}
            subtitle={`${systemStats.totalUniqueVisitors} unique`}
            icon={Eye} 
            trend={visitorTrends?.comparison?.uniqueVisitors?.change != null ? {
              value: Math.abs(visitorTrends.comparison.uniqueVisitors.change),
              positive: visitorTrends.comparison.uniqueVisitors.change >= 0,
            } : undefined}
          />
          <StatCard 
            id="masterThemes"
            title="Master Themes" 
            value={systemStats.totalMasterThemes}
            icon={Palette} 
            onClick={() => setTab('masterTheme')}
          />
        </Accordion>
      </div>

      {/* Visitor Trends Chart */}
      <Card className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h3 className="text-base sm:text-lg font-semibold flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Visitor Trends
          </h3>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative" ref={trendsDomainRef}>
              <button
                onClick={() => setTrendsDomainOpen(!trendsDomainOpen)}
                className="px-3 py-2 rounded-lg bg-secondary hover:bg-accent text-sm font-semibold text-foreground transition-colors flex items-center gap-2"
              >
                {trendsDomain
                  ? domainStats.find((d) => d.slug === trendsDomain)?.name || trendsDomain
                  : 'All Artists'}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className={`transition-transform ${trendsDomainOpen ? 'rotate-180' : ''}`}
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              {trendsDomainOpen && (
                <div className="absolute top-full left-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 min-w-[200px] max-h-64 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => { setTrendsDomain(''); setTrendsDomainOpen(false); }}
                    className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors ${
                      !trendsDomain
                        ? 'bg-primary/10 text-primary'
                        : 'text-foreground hover:bg-muted/50'
                    }`}
                  >
                    All Artists
                  </button>
                  {domainStats.map((d) => (
                    <button
                      key={d.slug}
                      onClick={() => { setTrendsDomain(d.slug); setTrendsDomainOpen(false); }}
                      className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors ${
                        trendsDomain === d.slug
                          ? 'bg-primary/10 text-primary'
                          : 'text-foreground hover:bg-muted/50'
                      }`}
                    >
                      {d.name || d.slug}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button
              variant={trendsDays === 'today' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setTrendsDays('today')}
            >
              Today
            </Button>
            <Button
              variant={trendsDays === 7 ? 'default' : 'outline'}
              size="sm"
              onClick={() => setTrendsDays(7)}
            >
              7 Days
            </Button>
            <Button
              variant={trendsDays === 30 ? 'default' : 'outline'}
              size="sm"
              onClick={() => setTrendsDays(30)}
            >
              30 Days
            </Button>
            <Button
              variant={trendsDays === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setTrendsDays('all')}
            >
              All Time
            </Button>
            <Button
              variant={trendsDays === 'range' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setTrendsDays('range')}
            >
              <Calendar className="w-4 h-4 mr-1.5" />
              Date Range
            </Button>
          </div>
        </div>

        {trendsDays === 'range' && (
          <div className="mb-4 flex justify-end">
            <div className="inline-block p-4 rounded-xl border border-border bg-muted/30 animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="flex flex-wrap items-end justify-end gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-muted-foreground">From</label>
                  <input
                    type="date"
                    value={trendsFrom}
                    max={trendsTo || undefined}
                    onChange={(e) => setTrendsFrom(e.target.value)}
                    className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-muted-foreground">To</label>
                  <input
                    type="date"
                    value={trendsTo}
                    min={trendsFrom || undefined}
                    onChange={(e) => setTrendsTo(e.target.value)}
                    className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>
                {(trendsFrom || trendsTo) && (
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-medium text-muted-foreground invisible">Clear</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setTrendsFrom(''); setTrendsTo(''); }}
                    >
                      Clear
                    </Button>
                  </div>
                )}
              </div>
              {trendsFrom && trendsTo && (
                <p className="mt-3 text-xs text-right text-muted-foreground">
                  Showing data from{' '}
                  <span className="font-medium text-foreground">
                    {new Date(trendsFrom + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium text-foreground">
                    {new Date(trendsTo + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </p>
              )}
            </div>
          </div>
        )}

        {/* Comparison Stats */}
        {visitorTrends?.comparison && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="p-3 rounded-lg bg-muted/30">
              <p className="text-xs text-muted-foreground">Total Visits</p>
              <p className="text-lg font-bold">{visitorTrends.comparison.sessions.current}</p>
              {visitorTrends.comparison.sessions.change !== null ? (
                <div className={`flex items-center gap-1 text-xs ${visitorTrends.comparison.sessions.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  <TrendingUp className={`w-3 h-3 ${visitorTrends.comparison.sessions.change < 0 && 'rotate-180'}`} />
                  <span>{Math.abs(visitorTrends.comparison.sessions.change)}%</span>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">Since start</p>
              )}
            </div>
            <div className="p-3 rounded-lg bg-muted/30">
              <p className="text-xs text-muted-foreground">Unique Visitors</p>
              <p className="text-lg font-bold">{visitorTrends.comparison.uniqueVisitors.current}</p>
              {visitorTrends.comparison.uniqueVisitors.change !== null ? (
                <div className={`flex items-center gap-1 text-xs ${visitorTrends.comparison.uniqueVisitors.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  <TrendingUp className={`w-3 h-3 ${visitorTrends.comparison.uniqueVisitors.change < 0 && 'rotate-180'}`} />
                  <span>{Math.abs(visitorTrends.comparison.uniqueVisitors.change)}%</span>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">Since start</p>
              )}
            </div>
            <div className="p-3 rounded-lg bg-muted/30">
              <p className="text-xs text-muted-foreground">Avg. Duration</p>
              <p className="text-lg font-bold">
                {visitorTrends.daily.length > 0
                  ? Math.round(visitorTrends.daily.reduce((s, d) => s + d.avgDurationSeconds, 0) / visitorTrends.daily.filter(d => d.avgDurationSeconds > 0).length || 1)
                  : 0}s
              </p>
              <p className="text-xs text-muted-foreground">per session</p>
            </div>
            <div className="p-3 rounded-lg bg-muted/30">
              <p className="text-xs text-muted-foreground">New vs Returning</p>
              <p className="text-lg font-bold">
                {visitorTrends.daily.reduce((s, d) => s + d.newVisitors, 0)} / {visitorTrends.daily.reduce((s, d) => s + d.returningVisitors, 0)}
              </p>
              <p className="text-xs text-muted-foreground">new / returning</p>
            </div>
          </div>
        )}

        {/* Chart */}
        {trendsLoading ? (
          <div className="flex items-center justify-center h-64">
            <Spinner size="lg" />
          </div>
        ) : visitorTrends && visitorTrends.daily.length > 0 ? (
          <ChartContainer
            config={visitorTrendsChartConfig}
            className="h-72 w-full"
          >
            <AreaChart data={visitorTrends.daily} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
              <defs>
                 <linearGradient id="fillSessions" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.4} />
                          <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0.05} />
                        </linearGradient>
                        <linearGradient id="fillUnique" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="var(--color-success)" stopOpacity={0.4} />
                          <stop offset="95%" stopColor="var(--color-success)" stopOpacity={0.05} />
                        </linearGradient>
              </defs>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(v: string) => {
                  const d = new Date(v + 'T00:00:00');
                  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                }}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                allowDecimals={false}
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    labelFormatter={(label: any) => {
                      const d = new Date(String(label) + 'T00:00:00');
                      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                    }}
                  />
                }
              />
              <ChartLegend content={<ChartLegendContent />} />
             <Area
                                     type="monotone"
                                     dataKey="sessions"
                                     stroke="var(--color-primary-light2)"
                                     fill="url(#fillSessions)"
                                     strokeWidth={2}
                                     stackId="a"
                                   />
                                   <Area
                                     type="monotone"
                                     dataKey="uniqueVisitors"
                                     stroke="var(--color-success)"
                                     fill="url(#fillUnique)"
                                     strokeWidth={2}
                                     stackId="b"
                                   />
                                 </AreaChart>
          </ChartContainer>
        ) : (
          <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">
            No visitor data available for this period
          </div>
        )}
      </Card>

      {/* Audience Breakdown — Geo & Device */}
      <Accordion
        type="multiple"
        value={expandedCards}
        onValueChange={handleExpandedChange}
        className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
      >
        {/* Top Countries */}
        <AccordionItem value="topCountries" className="group @container border-none">
          <Card className="p-0 overflow-hidden hover:shadow-none transition-shadow duration-200">
            <AccordionTrigger className="px-4 sm:px-5 py-4 hover:bg-muted/30 transition-colors">
              <Globe className="w-5 h-5 shrink-0 text-blue-500" />
              <h3 className="flex-1 min-w-0 text-left font-semibold text-sm text-foreground truncate">
                Top Countries
              </h3>
              {audienceBreakdown && audienceBreakdown.countries.length > 0 && (
                <Badge variant="secondary" className="shrink-0 tabular-nums group-data-[state=open]:hidden">
                  {audienceBreakdown.countries.length}
                </Badge>
              )}
            </AccordionTrigger>
            <AccordionContent className="px-4 sm:px-5 pb-4 pt-0">
              {breakdownLoading ? (
                <div className="flex items-center justify-center h-40"><Spinner /></div>
              ) : audienceBreakdown && audienceBreakdown.countries.length > 0 ? (
                <div className="space-y-2.5">
                  {audienceBreakdown.countries.map((c) => {
                    const pct = audienceBreakdown.totalSessions > 0 ? (c.sessions / audienceBreakdown.totalSessions) * 100 : 0;
                    return (
                      <div key={c.name} className="space-y-1">
                        <div className="flex items-center justify-between text-sm gap-2">
                          <span className="truncate font-medium min-w-0">{c.name}</span>
                          <span className="text-muted-foreground tabular-nums shrink-0">{c.sessions.toLocaleString()} <span className="text-xs">({pct.toFixed(1)}%)</span></span>
                        </div>
                        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                          <div className="h-full rounded-full bg-blue-500" style={{ width: `${Math.max(pct, 1)}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">No geo data available</div>
              )}
            </AccordionContent>
          </Card>
        </AccordionItem>

        {/* Top Cities */}
        <AccordionItem value="topCities" className="group @container border-none">
          <Card className="p-0 overflow-hidden hover:shadow-none transition-shadow duration-200">
            <AccordionTrigger className="px-4 sm:px-5 py-4 hover:bg-muted/30 transition-colors">
              <Activity className="w-5 h-5 shrink-0 text-emerald-500" />
              <h3 className="flex-1 min-w-0 text-left font-semibold text-sm text-foreground truncate">
                Top Cities
              </h3>
              {audienceBreakdown && audienceBreakdown.cities.length > 0 && (
                <Badge variant="secondary" className="shrink-0 tabular-nums group-data-[state=open]:hidden">
                  {audienceBreakdown.cities.length}
                </Badge>
              )}
            </AccordionTrigger>
            <AccordionContent className="px-4 sm:px-5 pb-4 pt-0">
              {breakdownLoading ? (
                <div className="flex items-center justify-center h-40"><Spinner /></div>
              ) : audienceBreakdown && audienceBreakdown.cities.length > 0 ? (
                <div className="space-y-2.5">
                  {audienceBreakdown.cities.map((c) => {
                    const pct = audienceBreakdown.totalSessions > 0 ? (c.sessions / audienceBreakdown.totalSessions) * 100 : 0;
                    return (
                      <div key={c.name} className="space-y-1">
                        <div className="flex items-center justify-between text-sm gap-2">
                          <span className="truncate font-medium min-w-0">{c.name}</span>
                          <span className="text-muted-foreground tabular-nums shrink-0">{c.sessions.toLocaleString()} <span className="text-xs">({pct.toFixed(1)}%)</span></span>
                        </div>
                        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                          <div className="h-full rounded-full bg-emerald-500" style={{ width: `${Math.max(pct, 1)}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">No city data available</div>
              )}
            </AccordionContent>
          </Card>
        </AccordionItem>

        {/* Device Breakdown */}
        <AccordionItem value="devices" className="group @container border-none">
          <Card className="p-0 overflow-hidden hover:shadow-none transition-shadow duration-200">
            <AccordionTrigger className="px-4 sm:px-5 py-4 hover:bg-muted/30 transition-colors">
              <Zap className="w-5 h-5 shrink-0 text-violet-500" />
              <h3 className="flex-1 min-w-0 text-left font-semibold text-sm text-foreground truncate">
                Devices
              </h3>
              {audienceBreakdown && audienceBreakdown.devices.length > 0 && (
                <Badge variant="secondary" className="shrink-0 tabular-nums group-data-[state=open]:hidden">
                  {audienceBreakdown.devices.length}
                </Badge>
              )}
            </AccordionTrigger>
            <AccordionContent className="px-4 sm:px-5 pb-4 pt-0">
              {breakdownLoading ? (
                <div className="flex items-center justify-center h-40"><Spinner /></div>
              ) : audienceBreakdown && audienceBreakdown.devices.length > 0 ? (
                <div className="space-y-2.5">
                  {audienceBreakdown.devices.map((d) => {
                    const pct = audienceBreakdown.totalSessions > 0 ? (d.sessions / audienceBreakdown.totalSessions) * 100 : 0;
                    const deviceIcon = d.name.toLowerCase().includes('mobile') ? '📱' : d.name.toLowerCase().includes('tablet') ? '📟' : '💻';
                    return (
                      <div key={d.name} className="space-y-1">
                        <div className="flex items-center justify-between text-sm gap-2">
                          <span className="truncate font-medium min-w-0">{deviceIcon} {d.name}</span>
                          <span className="text-muted-foreground tabular-nums shrink-0">{d.sessions.toLocaleString()} <span className="text-xs">({pct.toFixed(1)}%)</span></span>
                        </div>
                        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                          <div className="h-full rounded-full bg-violet-500" style={{ width: `${Math.max(pct, 1)}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">No device data available</div>
              )}
            </AccordionContent>
          </Card>
        </AccordionItem>

        {/* Browser Breakdown */}
        <AccordionItem value="browsers" className="group @container border-none">
          <Card className="p-0 overflow-hidden hover:shadow-none transition-shadow duration-200">
            <AccordionTrigger className="px-4 sm:px-5 py-4 hover:bg-muted/30 transition-colors">
              <Eye className="w-5 h-5 shrink-0 text-orange-500" />
              <h3 className="flex-1 min-w-0 text-left font-semibold text-sm text-foreground truncate">
                Browsers
              </h3>
              {audienceBreakdown && audienceBreakdown.browsers.length > 0 && (
                <Badge variant="secondary" className="shrink-0 tabular-nums group-data-[state=open]:hidden">
                  {audienceBreakdown.browsers.length}
                </Badge>
              )}
            </AccordionTrigger>
            <AccordionContent className="px-4 sm:px-5 pb-4 pt-0">
              {breakdownLoading ? (
                <div className="flex items-center justify-center h-40"><Spinner /></div>
              ) : audienceBreakdown && audienceBreakdown.browsers.length > 0 ? (
                <div className="space-y-2.5">
                  {audienceBreakdown.browsers.map((b) => {
                    const pct = audienceBreakdown.totalSessions > 0 ? (b.sessions / audienceBreakdown.totalSessions) * 100 : 0;
                    return (
                      <div key={b.name} className="space-y-1">
                        <div className="flex items-center justify-between text-sm gap-2">
                          <span className="truncate font-medium min-w-0">{b.name}</span>
                          <span className="text-muted-foreground tabular-nums shrink-0">{b.sessions.toLocaleString()} <span className="text-xs">({pct.toFixed(1)}%)</span></span>
                        </div>
                        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                          <div className="h-full rounded-full bg-orange-500" style={{ width: `${Math.max(pct, 1)}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">No browser data available</div>
              )}
            </AccordionContent>
          </Card>
        </AccordionItem>

        {/* OS Breakdown */}
        <AccordionItem value="operatingSystems" className="group @container border-none">
          <Card className="p-0 overflow-hidden hover:shadow-none transition-shadow duration-200">
            <AccordionTrigger className="px-4 sm:px-5 py-4 hover:bg-muted/30 transition-colors">
              <Settings className="w-5 h-5 shrink-0 text-cyan-500" />
              <h3 className="flex-1 min-w-0 text-left font-semibold text-sm text-foreground truncate">
                Operating Systems
              </h3>
              {audienceBreakdown && audienceBreakdown.oses.length > 0 && (
                <Badge variant="secondary" className="shrink-0 tabular-nums group-data-[state=open]:hidden">
                  {audienceBreakdown.oses.length}
                </Badge>
              )}
            </AccordionTrigger>
            <AccordionContent className="px-4 sm:px-5 pb-4 pt-0">
              {breakdownLoading ? (
                <div className="flex items-center justify-center h-40"><Spinner /></div>
              ) : audienceBreakdown && audienceBreakdown.oses.length > 0 ? (
                <div className="space-y-2.5">
                  {audienceBreakdown.oses.map((o) => {
                    const pct = audienceBreakdown.totalSessions > 0 ? (o.sessions / audienceBreakdown.totalSessions) * 100 : 0;
                    return (
                      <div key={o.name} className="space-y-1">
                        <div className="flex items-center justify-between text-sm gap-2">
                          <span className="truncate font-medium min-w-0">{o.name}</span>
                          <span className="text-muted-foreground tabular-nums shrink-0">{o.sessions.toLocaleString()} <span className="text-xs">({pct.toFixed(1)}%)</span></span>
                        </div>
                        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                          <div className="h-full rounded-full bg-cyan-500" style={{ width: `${Math.max(pct, 1)}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">No OS data available</div>
              )}
            </AccordionContent>
          </Card>
        </AccordionItem>
      </Accordion>

      {/* Pending Approvals */}
      {pendingDomains.length > 0 && (
        <Card className="p-4 sm:p-6 border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h3 className="text-base sm:text-lg font-semibold flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-600" />
              Pending Review
              <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">
                {pendingDomains.length}
              </Badge>
            </h3>
            <Button variant="ghost" size="sm" onClick={() => setTab('domain')}>
              View All <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="space-y-3">
            {pendingDomains.map((domain) => (
              <div
                key={domain._id}
                className="flex items-center justify-between p-3 rounded-lg bg-white dark:bg-muted/30 border"
              >
                <div className="flex items-center gap-3">
                  <Globe className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{domain.slug}</p>
                    <p className="text-xs text-muted-foreground">
                      Completed {new Date(domain.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openPreview(domain.slug).catch(() => toast.error('Failed to open preview'))}
                    title="Preview website before approving"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Preview
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-green-600 border-green-300 hover:bg-green-50"
                    disabled={approvingSlug === domain.slug}
                    onClick={() => handleQuickApprove(domain.slug)}
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    {approvingSlug === domain.slug ? 'Approving...' : 'Approve'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 border-red-300 hover:bg-red-50"
                    onClick={() => setTab('domain')}
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Review
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Content Stats Across All Domains */}
      <div>
        <h2 className="text-base sm:text-lg font-semibold mb-4 flex items-center gap-2">
          <PieChart className="w-5 h-5" />
          Global Content Overview
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="p-4 text-center">
            <Music className="w-8 h-8 mx-auto mb-2 text-primary" />
            <p className="text-xl sm:text-2xl font-bold">{contentOverview.totalMusic}</p>
            <p className="text-xs text-muted-foreground">Music Tracks</p>
          </Card>
          <Card className="p-4 text-center">
            <Video className="w-8 h-8 mx-auto mb-2 text-primary" />
            <p className="text-xl sm:text-2xl font-bold">{contentOverview.totalVideos}</p>
            <p className="text-xs text-muted-foreground">Videos</p>
          </Card>
          <Card className="p-4 text-center">
            <Image className="w-8 h-8 mx-auto mb-2 text-primary" />
            <p className="text-xl sm:text-2xl font-bold">{contentOverview.totalImages}</p>
            <p className="text-xs text-muted-foreground">Images</p>
          </Card>
          <Card className="p-4 text-center">
            <Mail className="w-8 h-8 mx-auto mb-2 text-primary" />
            <p className="text-xl sm:text-2xl font-bold">{totalLeads}</p>
            <p className="text-xs text-muted-foreground">Total Leads</p>
          </Card>
          <Card className="p-4 text-center">
            <Calendar className="w-8 h-8 mx-auto mb-2 text-primary" />
            <p className="text-xl sm:text-2xl font-bold">{contentOverview.totalEvents}</p>
            <p className="text-xs text-muted-foreground">Events</p>
          </Card>
          <Card className="p-4 text-center">
            <Zap className="w-8 h-8 mx-auto mb-2 text-primary" />
            <p className="text-xl sm:text-2xl font-bold">{totalContent}</p>
            <p className="text-xs text-muted-foreground">Total Content</p>
          </Card>
        </div>
      </div>

      {/* Domain Performance Table */}
      <Card className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h3 className="text-base sm:text-lg font-semibold flex items-center gap-2">
            <Globe className="w-5 h-5" />
            Domain Statistics
          </h3>
          <Button variant="ghost" size="sm" onClick={() => setTab('domain')}>
            Manage Domains <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
        
        {domainStats.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Domain</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Music</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Videos</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Images</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Leads</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Events</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Visitors</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Unique</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Total</th>
                </tr>
              </thead>
              <tbody>
                {domainStats.map((domain) => (
                  <tr key={domain.slug} className="border-b hover:bg-muted/50 transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-muted-foreground" />
                        <span className="font-medium">{domain.slug}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center">
                      {domain.isActive ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">
                          <XCircle className="w-3 h-3 mr-1" />
                          Inactive
                        </Badge>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="inline-flex items-center gap-1">
                        <Music className="w-3 h-3 text-primary" />
                        {domain.music}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="inline-flex items-center gap-1">
                        <Video className="w-3 h-3 text-primary" />
                        {domain.videos}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="inline-flex items-center gap-1">
                        <Image className="w-3 h-3 text-primary" />
                        {domain.images}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="inline-flex items-center gap-1">
                        <Mail className="w-3 h-3 text-primary" />
                        {domain.leads}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-primary" />
                        {domain.events}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="inline-flex items-center gap-1">
                        <Eye className="w-3 h-3 text-primary" />
                        {domain.totalVisitors}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="inline-flex items-center gap-1">
                        <UserCheck className="w-3 h-3 text-primary" />
                        {domain.uniqueVisitors}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <Badge variant="secondary">{domain.totalContent}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Globe className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No domains found</p>
          </div>
        )}
      </Card>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <Card className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h3 className="text-base sm:text-lg font-semibold flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Recent Activity
            </h3>
          </div>
          {recentActivities.length > 0 ? (
            <div className="space-y-3">
              {recentActivities.map((activity) => (
                <div 
                  key={activity._id} 
                  className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                >
                  <div className={`p-2 rounded-lg ${
                    activity.type === 'lead' ? 'bg-amber-100 dark:bg-amber-900/30' :
                    activity.type === 'content' ? 'bg-blue-100 dark:bg-blue-900/30' :
                    'bg-purple-100 dark:bg-purple-900/30'
                  }`}>
                    {activity.type === 'lead' ? (
                      <Mail className="w-4 h-4 text-amber-600" />
                    ) : activity.type === 'content' ? (
                      <Eye className="w-4 h-4 text-blue-600" />
                    ) : (
                      <Users className="w-4 h-4 text-purple-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{activity.message}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {activity.domain && (
                        <Badge variant="outline" className="text-xs">
                          {activity.domain}
                        </Badge>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {new Date(activity.timestamp).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">
              No recent activity
            </p>
          )}
        </Card>

        {/* Quick Actions */}
        <Card className="p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Quick Actions
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button 
              variant="outline" 
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => setTab('domain')}
            >
              <Globe className="w-6 h-6 text-primary" />
              <span className="text-sm">Add Domain</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => setTab('user')}
            >
              <Users className="w-6 h-6 text-primary" />
              <span className="text-sm">Manage Users</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => setTab('masterTheme')}
            >
              <Palette className="w-6 h-6 text-primary" />
              <span className="text-sm">Themes</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => setTab('masterPlatforms')}
            >
              <Share2 className="w-6 h-6 text-primary" />
              <span className="text-sm">Platforms</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => setTab('serverImages')}
            >
              <HardDrive className="w-6 h-6 text-primary" />
              <span className="text-sm">Server Media</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => setTab('awardAssets')}
            >
              <Database className="w-6 h-6 text-primary" />
              <span className="text-sm">Award Assets</span>
            </Button>
          </div>
        </Card>
      </div>

      {/* Domain Content Distribution */}
      {domainStats.length > 0 && (
        <Card className="p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Content Distribution by Domain
          </h3>
          <div className="space-y-4">
            {domainStats
              .filter(d => d.totalContent > 0)
              .sort((a, b) => b.totalContent - a.totalContent)
              .slice(0, 5)
              .map((domain) => {
                const maxContent = Math.max(...domainStats.map(d => d.totalContent));
                const percentage = maxContent > 0 ? (domain.totalContent / maxContent) * 100 : 0;
                
                return (
                  <div key={domain.slug} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{domain.slug}</span>
                      <span className="text-sm text-muted-foreground">{domain.totalContent} items</span>
                    </div>
                    <Progress value={percentage} className="h-2" />
                  </div>
                );
              })}
          </div>
        </Card>
      )}
    </div>
  );
};

export default SuperAdminDashboard;
